// Tiny DOM helpers for the loader: elements are created and filled with textContent only (no innerHTML), and styles
// go through CSS classes and custom properties, so the loader works under strict CSP and Trusted Types.

type Attrs = Record<string, string | number | boolean | null | undefined>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    node.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children) node.append(c);
  return node;
}

const SVG = 'http://www.w3.org/2000/svg';

export function svg(paths: string[], o: { size?: number; cls?: string; fill?: boolean } = {}): SVGSVGElement {
  const s = document.createElementNS(SVG, 'svg');
  s.setAttribute('viewBox', '0 0 24 24');
  s.setAttribute('width', String(o.size ?? 24));
  s.setAttribute('height', String(o.size ?? 24));
  s.setAttribute('aria-hidden', 'true');
  s.setAttribute('focusable', 'false');
  if (o.cls) s.setAttribute('class', o.cls);
  for (const d of paths) {
    const p = document.createElementNS(SVG, 'path');
    p.setAttribute('d', d);
    if (o.fill) p.setAttribute('fill', 'currentColor');
    else {
      p.setAttribute('fill', 'none');
      p.setAttribute('stroke', 'currentColor');
      p.setAttribute('stroke-width', '2');
      p.setAttribute('stroke-linecap', 'round');
      p.setAttribute('stroke-linejoin', 'round');
    }
    s.append(p);
  }
  return s;
}

export const ICONS = {
  chat: ['M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.9A8 8 0 1 1 21 12Z'],
  close: ['M6 6l12 12', 'M18 6 6 18'],
};

/** Set CSS custom properties (the only inline styling the loader does; it's CSSOM, which CSP allows). */
export function vars(node: HTMLElement, values: Record<string, string | number | null>) {
  for (const [k, v] of Object.entries(values)) {
    if (v === null) node.style.removeProperty(k);
    else node.style.setProperty(k, typeof v === 'number' ? `${v}px` : v);
  }
}

/** Dark or light text for a background colour (WCAG relative luminance). */
export function onColor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#ffffff';
  const n = Number.parseInt(m[1]!, 16);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return l > 0.45 ? '#0b1020' : '#ffffff';
}

/** Adopt a stylesheet into a shadow root (CSSOM is not governed by style-src), falling back to <style nonce>. */
export function adoptStyles(root: ShadowRoot, css: string, nonce: string | null, fallbackHref: string) {
  try {
    if (
      'adoptedStyleSheets' in root &&
      typeof CSSStyleSheet !== 'undefined' &&
      'replaceSync' in CSSStyleSheet.prototype
    ) {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      return;
    }
  } catch {
    // fall through
  }
  if (nonce) {
    const style = el('style', { nonce });
    style.textContent = css;
    root.append(style);
    return;
  }
  root.append(el('link', { rel: 'stylesheet', href: fallbackHref }));
}
